import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Switch } from "../../ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { FolderOpen, Combine, EyeOff } from "lucide-react";
import { STEP_TYPES } from "./configuratorConstants";

const StepDialog = ({
  open,
  onOpenChange,
  isDark,
  editingStep,
  stepForm,
  setStepForm,
  steps,
  onSave,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`sm:max-w-md max-h-[85vh] overflow-y-auto ${isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white"}`}
      >
        <DialogHeader>
          <DialogTitle className={isDark ? "text-white" : ""}>
            {editingStep ? "Stap Bewerken" : "Nieuwe Stap"}
          </DialogTitle>
          <DialogDescription>
            {editingStep
              ? "Pas de instellingen van deze stap aan."
              : "Voeg een nieuwe stap toe aan je configurator."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className={isDark ? "text-gray-300" : ""}>Naam *</Label>
            <Input
              value={stepForm.name}
              onChange={(e) =>
                setStepForm((p) => ({ ...p, name: e.target.value }))
              }
              placeholder="bv. Type Fiets, Kleur, Maat..."
              className={
                isDark
                  ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600"
                  : "border-gray-200"
              }
            />
          </div>
          <div className="space-y-2">
            <Label className={isDark ? "text-gray-300" : ""}>
              Beschrijving
            </Label>
            <Textarea
              value={stepForm.description}
              onChange={(e) =>
                setStepForm((p) => ({ ...p, description: e.target.value }))
              }
              placeholder="Optionele instructie voor de klant..."
              rows={2}
              className={
                isDark
                  ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600"
                  : "border-gray-200"
              }
            />
          </div>
          <div className="space-y-2">
            <Label className={isDark ? "text-gray-300" : ""}>Type</Label>
            <div className="grid grid-cols-2 gap-2">
              {STEP_TYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() =>
                    setStepForm((p) => ({ ...p, type: type.value }))
                  }
                  className={`p-3 rounded-lg border text-left transition-all ${
                    stepForm.type === type.value
                      ? isDark
                        ? "border-white/20 bg-white/[0.04]"
                        : "border-gray-400 bg-gray-50"
                      : isDark
                        ? "border-white/[0.06] hover:border-white/10"
                        : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <p
                    className={`font-medium text-sm ${isDark ? "text-white" : "text-gray-900"}`}
                  >
                    {type.label}
                  </p>
                  <p
                    className={`text-xs mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {type.description}
                  </p>
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className={isDark ? "text-gray-300" : ""}>Verplicht</Label>
              <p
                className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
              >
                Klant moet een keuze maken
              </p>
            </div>
            <Switch
              checked={stepForm.required}
              onCheckedChange={(checked) =>
                setStepForm((p) => ({ ...p, required: checked }))
              }
            />
          </div>
          {/* Group */}
          <div className="space-y-2">
            <Label className={isDark ? "text-gray-300" : ""}>
              Groep (optioneel)
            </Label>
            <p
              className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
            >
              Stappen met dezelfde groep worden samen op één pagina getoond.
            </p>
            <Input
              value={stepForm.group}
              onChange={(e) =>
                setStepForm((p) => ({ ...p, group: e.target.value }))
              }
              placeholder="bv. Wielen, Frame, Afwerking..."
              className={
                isDark
                  ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600"
                  : "border-gray-200"
              }
            />
            {(() => {
              const existingGroups = [
                ...new Set(
                  steps.map((s) => s.group).filter((g) => g && g.trim()),
                ),
              ];
              if (existingGroups.length === 0) return null;
              return (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {existingGroups.map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setStepForm((p) => ({ ...p, group: g }))}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                        stepForm.group === g
                          ? isDark
                            ? "bg-white text-black"
                            : "bg-gray-900 text-white"
                          : isDark
                            ? "bg-white/[0.06] text-gray-400 hover:bg-white/10"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      <FolderOpen className="h-3 w-3 inline mr-1" />
                      {g}
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>
          {/* Image mode */}
          <div className="flex items-center justify-between">
            <div>
              <Label
                className={`flex items-center space-x-1.5 ${isDark ? "text-gray-300" : ""}`}
              >
                <Combine className="h-3.5 w-3.5" />
                <span>Afbeeldingen stapelen</span>
              </Label>
              <p
                className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
              >
                Foto's van deze stap worden op vorige lagen gelegd (bv.
                fietsonderdelen die samen 1 beeld vormen)
              </p>
            </div>
            <Switch
              checked={stepForm.imageMode === "stack"}
              onCheckedChange={(checked) =>
                setStepForm((p) => ({
                  ...p,
                  imageMode: checked ? "stack" : "replace",
                  hiddenLayer: checked ? p.hiddenLayer : false,
                }))
              }
            />
          </div>

          {/* Hidden layer (only for stacked steps) */}
          {stepForm.imageMode === "stack" && (
            <div className="flex items-center justify-between">
              <div>
                <Label
                  className={`flex items-center space-x-1.5 ${isDark ? "text-gray-300" : ""}`}
                >
                  <EyeOff className="h-3.5 w-3.5" />
                  <span>Verborgen laag</span>
                </Label>
                <p
                  className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                >
                  Stap wordt niet getoond bij selectie, maar de afbeelding
                  verschijnt wel als laag in de image viewer
                </p>
              </div>
              <Switch
                checked={stepForm.hiddenLayer || false}
                onCheckedChange={(checked) =>
                  setStepForm((p) => ({
                    ...p,
                    hiddenLayer: checked,
                  }))
                }
              />
            </div>
          )}

          {stepForm.type === "frame-color" && (
            <div
              className={`rounded-lg border p-3 ${isDark ? "border-white/[0.06] bg-white/[0.02]" : "border-gray-100 bg-gray-50"}`}
            >
              <p
                className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
              >
                Voeg opties toe aan deze stap. Elke optie is een frame-laag met
                een eigen afbeelding, standaardkleur en gradient.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className={
              isDark
                ? "border-white/[0.06] text-gray-300 hover:bg-white/[0.04]"
                : ""
            }
          >
            Annuleren
          </Button>
          <Button
            onClick={onSave}
            className={
              isDark
                ? "bg-white text-black hover:bg-gray-200"
                : "bg-gray-900 text-white hover:bg-gray-800"
            }
            disabled={!stepForm.name.trim()}
          >
            {editingStep ? "Opslaan" : "Toevoegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StepDialog;
